import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, TrendingUp, Users, Shield, Zap, Globe } from 'lucide-react';

interface Country {
  dimensions?: {
    clientCountryName?: string;
    date?: string;
  };
  sum?: {
    requests?: number;
    bytes?: number;
    pageViews?: number;
  };
}

interface BotData {
  dimensions?: {
    datetime?: string;
    date?: string;
    datetimeHour?: string;
  };
  sum?: {
    requests: number;
    botRequests: number;
  };
}

interface AnalyticsModalProps {
  isOpen: boolean;
  domainName: string;
  domainId: number;
  onClose: () => void;
}

const AnalyticsModal: React.FC<AnalyticsModalProps> = ({
  isOpen,
  domainName,
  domainId,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'traffic' | 'security' | 'performance' | 'bot'>('overview');
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [topCountries, setTopCountries] = useState<Country[]>([]);
  const [botData, setBotData] = useState<BotData[]>([]);

  useEffect(() => {
    if (isOpen && domainId) {
      loadAnalytics();
    }
  }, [isOpen, domainId]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [analyticsRes, countriesRes, botRes] = await Promise.all([
        fetch(`/api/domains/${domainId}/analytics`, { credentials: 'include' }),
        fetch(`/api/domains/${domainId}/analytics/countries`, { credentials: 'include' }).catch(() => null),
        fetch(`/api/domains/${domainId}/analytics/bot-traffic`, { credentials: 'include' }).catch(() => null)
      ]);

      if (!analyticsRes.ok) {
        const errorData = await analyticsRes.json();
        throw new Error(errorData.error || 'Failed to load analytics');
      }

      const analyticsJson = await analyticsRes.json();
      if (analyticsJson.success) {
        setAnalyticsData(analyticsJson.analytics);
      }

      if (countriesRes?.ok) {
        const countriesJson = await countriesRes.json();
        if (countriesJson.success) {
          setTopCountries(countriesJson.data || []);
        }
      }

      if (botRes?.ok) {
        const botJson = await botRes.json();
        if (botJson.success) {
          setBotData(botJson.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError((error as Error).message || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card-bg border border-border-default rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border-default">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">Analytics</h2>
            <p className="text-sm text-text-muted">{domainName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        {error && (
          <div className="m-6 p-4 bg-accent-red/10 border border-accent-red/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-accent-red" />
              <p className="text-accent-red text-sm">{error}</p>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center h-96">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 text-accent-blue animate-spin mx-auto" />
              <p className="text-text-muted">Loading analytics...</p>
            </div>
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="flex border-b border-border-default overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-6 py-4 font-semibold text-sm transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'text-accent-blue border-accent-blue'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>Overview</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('traffic')}
                className={`px-6 py-4 font-semibold text-sm transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'traffic'
                    ? 'text-accent-blue border-accent-blue'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4" />
                  <span>HTTP Traffic</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`px-6 py-4 font-semibold text-sm transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'security'
                    ? 'text-accent-blue border-accent-blue'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Security</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`px-6 py-4 font-semibold text-sm transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'performance'
                    ? 'text-accent-blue border-accent-blue'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Zap className="w-4 h-4" />
                  <span>Performance</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('bot')}
                className={`px-6 py-4 font-semibold text-sm transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'bot'
                    ? 'text-accent-blue border-accent-blue'
                    : 'text-text-muted border-transparent hover:text-text-primary'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Bot Traffic</span>
                </div>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-table-bg rounded-lg p-4 border border-border-default">
                      <h3 className="text-sm font-semibold text-text-muted mb-4">Web Analytics</h3>
                      {analyticsData?.webAnalytics ? (
                        <div className="text-sm text-text-primary space-y-2">
                          <p className="text-text-muted">Analysis by country available</p>
                        </div>
                      ) : (
                        <p className="text-text-muted text-sm">No web analytics data available</p>
                      )}
                    </div>

                    <div className="bg-table-bg rounded-lg p-4 border border-border-default">
                      <h3 className="text-sm font-semibold text-text-muted mb-4">Performance</h3>
                      {analyticsData?.performanceAnalytics ? (
                        <div className="text-sm text-text-primary space-y-2">
                          <p className="text-text-muted">Hourly performance metrics available</p>
                        </div>
                      ) : (
                        <p className="text-text-muted text-sm">No performance data available</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-table-bg rounded-lg p-4 border border-border-default">
                    <h3 className="text-sm font-semibold text-text-muted mb-4">Top Countries by Traffic</h3>
                    {topCountries && topCountries.length > 0 ? (
                      <div className="space-y-3">
                        {topCountries.slice(0, 5).map((country: Country, idx: number) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-text-primary">
                              {country.dimensions?.clientCountryName || 'Unknown'}
                            </span>
                            <div className="flex items-center space-x-4">
                              <div className="bg-accent-blue/20 rounded-lg px-3 py-1">
                                <span className="text-sm font-semibold text-accent-blue">
                                  {country.sum?.requests || 0} requests
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-text-muted text-sm">No country data available</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'traffic' && (
                <div className="space-y-4">
                  <div className="bg-table-bg rounded-lg p-6 border border-border-default">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">HTTP Traffic Analysis</h3>
                    <p className="text-text-muted text-sm mb-6">
                      Monitor your domain's HTTP traffic patterns and identify traffic distribution across different request types.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-accent-blue/10 border border-accent-blue/20 rounded-lg">
                        <p className="text-sm text-accent-blue">
                          HTTP traffic metrics include requests, threats detection, page views, and bandwidth consumption. Use this data to understand traffic patterns and identify anomalies.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-4">
                  <div className="bg-table-bg rounded-lg p-6 border border-border-default">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Security Analytics</h3>
                    <p className="text-text-muted text-sm mb-6">
                      Monitor security threats and malicious requests targeting your domain.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-accent-purple/10 border border-accent-purple/20 rounded-lg">
                        <p className="text-sm text-accent-purple">
                          Security metrics include threat data and bot score information. These metrics help identify potential attacks and suspicious activities.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'performance' && (
                <div className="space-y-4">
                  <div className="bg-table-bg rounded-lg p-6 border border-border-default">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Performance Analytics</h3>
                    <p className="text-text-muted text-sm mb-6">
                      Analyze your domain's performance metrics including cache hit rates and SSL timing.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-accent-green/10 border border-accent-green/20 rounded-lg">
                        <p className="text-sm text-accent-green">
                          Performance metrics include SSL timing, cached bytes ratio, and overall bandwidth consumption. Higher cache hit rates indicate better performance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'bot' && (
                <div className="space-y-4">
                  <div className="bg-table-bg rounded-lg p-6 border border-border-default">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">Bot Traffic Analysis</h3>
                    <p className="text-text-muted text-sm mb-6">
                      Understand and reduce bot traffic to your domain. Analyze the ratio of bot requests vs. legitimate traffic.
                    </p>
                    
                    {botData && botData.length > 0 ? (
                      <div className="space-y-4">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border-default">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-text-muted">DateTime</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-muted">Total Requests</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-muted">Bot Requests</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-muted">Bot %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {botData.map((data: BotData, idx: number) => {
                                const totalReqs = data.sum?.requests || 0;
                                const botReqs = data.sum?.botRequests || 0;
                                const botPercent = totalReqs > 0 ? ((botReqs / totalReqs) * 100).toFixed(2) : '0.00';
                                const dateStr = data.dimensions?.datetime || data.dimensions?.date || '';
                                
                                return (
                                  <tr key={idx} className="border-b border-border-default hover:bg-table-row-hover">
                                    <td className="py-3 px-4 text-sm text-text-primary">
                                      {new Date(dateStr).toLocaleDateString()}
                                    </td>
                                    <td className="py-3 px-4 text-right text-sm text-text-primary">{totalReqs}</td>
                                    <td className="py-3 px-4 text-right text-sm text-accent-red font-semibold">{botReqs}</td>
                                    <td className="py-3 px-4 text-right text-sm">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        parseFloat(botPercent) > 50 
                                          ? 'bg-accent-red/10 text-accent-red' 
                                          : 'bg-accent-yellow/10 text-accent-yellow'
                                      }`}>
                                        {botPercent}%
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="p-4 bg-accent-yellow/10 border border-accent-yellow/20 rounded-lg">
                          <p className="text-sm text-accent-yellow">
                            High bot traffic percentage indicates that a significant portion of your traffic is from automated sources. Consider enabling bot management rules to reduce bot traffic.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-text-muted text-sm">No bot traffic data available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="border-t border-border-default p-6 flex items-center justify-between">
          <p className="text-xs text-text-muted">
            {analyticsData?.timestamp ? `Last updated: ${new Date(analyticsData.timestamp).toLocaleString()}` : 'Analytics data'}
          </p>
          <button
            onClick={onClose}
            className="btn-secondary px-6"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal;
